"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  FileSearch,
  FileText,
  Languages,
  ListChecks,
  MessageSquareText,
  Search,
  ShieldAlert,
} from "lucide-react";
import RequestDemoCTA from "@/components/site/Header/cta/RequestDemoCTA";
import FinalCtaSection from "@/components/site/SeeInAction/FinalCtaSection";
import { useI18n } from "@/locales/client";

type Benefit = {
  title: string;
  body: string;
};

type Capability = {
  icon: LucideIcon;
  title: string;
  body: string;
};

const userDemoText =
  "Show incident trends for forklift near-misses in the last 90 days and draft a supervisor summary.";
const botDemoText =
  "I found 14 relevant incidents across shipping and staging zones. Near-misses increased 22% week-over-week near dock lanes 3 and 4. I drafted a supervisor summary and linked the supporting records.";

const userTypingSpeedMs = 24;
const botTypingSpeedMs = 16;
const userStartDelayMs = 300;
const botStartDelayMs =
  userStartDelayMs + userDemoText.length * userTypingSpeedMs + 700;
const animationCycleMs =
  botStartDelayMs + botDemoText.length * botTypingSpeedMs + 2200;
const reportRevealDelayMs =
  botStartDelayMs + botDemoText.length * botTypingSpeedMs + 500;

type TypewriterTextProps = {
  text: string;
  delay: number;
  speed: number;
  cycle: number;
};

function TypewriterText({
  text,
  delay,
  speed,
  cycle,
}: TypewriterTextProps) {
  const [visibleText, setVisibleText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setVisibleText("");
    setIsTyping(false);

    let currentIndex = 0;
    let typingInterval: ReturnType<typeof setInterval> | undefined;

    const startTimer = setTimeout(() => {
      setIsTyping(true);

      typingInterval = setInterval(() => {
        currentIndex += 1;
        setVisibleText(text.slice(0, currentIndex));

        if (currentIndex >= text.length) {
          setIsTyping(false);

          if (typingInterval) {
            clearInterval(typingInterval);
          }
        }
      }, speed);
    }, delay);

    return () => {
      clearTimeout(startTimer);

      if (typingInterval) {
        clearInterval(typingInterval);
      }
    };
  }, [cycle, delay, speed, text]);

  return (
    <div className="relative">
      <span className="invisible block">{text}</span>
      <span className="absolute inset-0 block">
        {visibleText}
        <span
          className={`ml-1 inline-block h-[1.05em] w-[2px] translate-y-[0.18em] rounded-full bg-current ${
            isTyping ? "animate-pulse" : "opacity-0"
          }`}
          aria-hidden="true"
        />
      </span>
    </div>
  );
}

export default function ChatBot() {
  const t = useI18n();
  const [animationCycle, setAnimationCycle] = useState(0);
  const [showBotBubble, setShowBotBubble] = useState(false);
  const [showReportCard, setShowReportCard] = useState(false);
  const benefits: Benefit[] = [
    {
      title: t("understand-risks-faster"),
      body: t("leverage-ai-to-uncover-hidden-safety-risks"),
    },
    {
      title: t("generate-safety-documents-instantly"),
      body: t(
        "create-risk-assessments-checklists-and-investigation-reports-in-seconds",
      ),
    },
    {
      title: t("seamless-integration"),
      body: t("connect-with-your-existing-safety-systems"),
    },
  ];

  const capabilities: Capability[] = [
    {
      icon: Search,
      title: t("grounded-document-search"),
      body: t("ask-questions-across-all-your-safety-procedures"),
    },
    {
      icon: BarChart3,
      title: t("incident-data-analysis"),
      body: t("upload-your-osha-log"),
    },
    {
      icon: ShieldAlert,
      title: t("risk-assessment-drafting"),
      body: t("generate-complete-jha-and-jsa-drafts"),
    },
    {
      icon: FileSearch,
      title: t("root-cause-analysis"),
      body: t("5-whys-rca-with-full-document-grounding"),
    },
    {
      icon: ListChecks,
      title: t("safety-checklist-generation"),
      body: t("generate-task-specific-inspection-checklists-in-seconds"),
    },
    {
      icon: Languages,
      title: t("multilingual-by-default"),
      body: t("work-in-english-french-or-spanish"),
    },
  ];

  const steps = [
    {
      number: "1",
      title: t("ask-a-question"),
      body: t("describe-the-safety-issue"),
    },
    {
      number: "2",
      title: t("the-assistant-analyzes-your-data"),
      body: t("it-searches-procedures"),
    },
    {
      number: "3",
      title: t("insights-are-generated"),
      body: t("trends-risks-and-recommendations-are-identified-automatically"),
    },
    {
      number: "4",
      title: t("you-receive-a-ready-to-use-output"),
      body: t("reports-charts-or-documents-ready-for-review-and-action"),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationCycle((current) => current + 1);
    }, animationCycleMs);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setShowBotBubble(false);

    const timer = setTimeout(() => {
      setShowBotBubble(true);
    }, botStartDelayMs);

    return () => clearTimeout(timer);
  }, [animationCycle]);

  useEffect(() => {
    setShowReportCard(false);

    const timer = setTimeout(() => {
      setShowReportCard(true);
    }, reportRevealDelayMs);

    return () => clearTimeout(timer);
  }, [animationCycle]);

  return (
    <main className="bg-[#f4efe7] text-slate-950">
      <section className="relative overflow-hidden bg-[#111111] text-white">
        <div className="absolute inset-0">
          <Image
            src="/products/chatbot-hero-v3.webp"
            alt=""
            aria-hidden="true"
            fill
            priority
            sizes="100vw"
            className="object-cover object-center"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(17,17,17,0.68)_0%,rgba(17,17,17,0.56)_35%,rgba(255,139,73,0.72)_100%)]" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#ffb184]">
              {t("solyntek-ai")}
            </p>
            <h1 className="mt-6 text-5xl font-semibold tracking-tight text-white md:text-6xl lg:text-7xl">
              {t("solyntek-copilot-your-ai-safety-assistant")}
            </h1>
            <p className="mt-6 max-w-3xl text-xl leading-9 text-white/88 md:text-2xl">
              {t("solyntek-copilot-automates-safety-monitoring")}
            </p>
            <div className="mt-8 mb-6 flex flex-wrap gap-4 lg:mb-0">
              <RequestDemoCTA />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f5f1ea] py-20">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          {benefits.map((item) => (
            <div
              key={item.title}
              className="rounded-[28px] border border-black/8 bg-white p-7 shadow-[0_20px_60px_rgba(15,23,42,0.06)]"
            >
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                {item.title}
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#111111] py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#ff8b49]">
              {t("how-it-works")}
            </p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight text-white md:text-5xl">
              {t("built-for-enterprise-integration")}
            </h2>
            <p className="mt-5 text-lg leading-8 text-white/72">
              {t("solyntek-plugs-into-analytics")}
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {capabilities.map((item) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.title}
                  className="rounded-[24px] border border-white/10 bg-white/5 p-6 shadow-[0_25px_80px_rgba(0,0,0,0.22)]"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ff8b49]/14 text-[#ffb184]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-[1.75rem] font-semibold leading-tight text-white">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-white/70">{item.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        id="assistant-demo"
        className="bg-[linear-gradient(180deg,#f7f3ed_0%,#efe5d8_100%)] py-20"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
          <div className="lg:self-center">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#ff8b49]">
              In action
            </p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              {t("ask-a-question")}
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-700">
              {t("describe-the-safety-issue")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <RequestDemoCTA />
            </div>
          </div>

          <div className="relative min-h-[980px] sm:min-h-[840px] lg:min-h-[560px]">
            <div
              id="chat-preview"
              aria-hidden={showReportCard}
              className={`rounded-[30px] border border-black/8 bg-white p-6 shadow-[0_25px_80px_rgba(15,23,42,0.08)] transition-all duration-500 absolute inset-0 ${
                showReportCard
                  ? "pointer-events-none translate-y-6 scale-[0.98] opacity-0"
                  : "translate-y-0 scale-100 opacity-100"
              }`}
            >
              <div className="flex items-center justify-between border-b border-black/6 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#111111] text-white">
                    <MessageSquareText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {t("user")}
                    </p>
                    <p className="text-xl font-semibold text-slate-950">
                      Solyntek Assistant
                    </p>
                  </div>
                </div>
                <div className="rounded-full border border-[#ff8b49]/30 bg-[#ff8b49]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#d96829]">
                  Ready
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div className="max-w-[80%] rounded-[24px] rounded-tl-[8px] bg-slate-100 px-4 py-3 text-sm leading-7 text-slate-700">
                  <TypewriterText
                    text={userDemoText}
                    delay={userStartDelayMs}
                    speed={userTypingSpeedMs}
                    cycle={animationCycle}
                  />
                </div>
                {showBotBubble ? (
                  <div className="ml-auto max-w-[88%] rounded-[24px] rounded-tr-[8px] bg-[#111111] px-4 py-3 text-sm leading-7 text-white">
                    <TypewriterText
                      text={botDemoText}
                      delay={0}
                      speed={botTypingSpeedMs}
                      cycle={animationCycle}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div
              aria-hidden={!showReportCard}
              className={`rounded-[30px] border border-black/8 bg-white p-6 shadow-[0_25px_80px_rgba(15,23,42,0.08)] transition-all duration-500 absolute inset-0 ${
                showReportCard
                  ? "translate-y-0 scale-100 opacity-100"
                  : "pointer-events-none translate-y-8 scale-[0.98] opacity-0"
              }`}
            >
              <div className="flex items-center justify-between border-b border-black/6 pb-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("copilot")}
                  </p>
                  <p className="text-2xl font-semibold text-slate-950">
                    {t("safety-debrief-forklift-near-miss-trends")}
                  </p>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#ff8b49]/10 text-[#d96829]">
                  <FileText className="h-5 w-5" />
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("summary")}
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-700">
                    {t("elevated-vehicle-pedestrian-risk")}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("recommended-action")}
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-700">
                    {t("add-temporary-lane-controls")}
                  </p>
                </div>
                <div className="rounded-2xl bg-[#ff8b49]/10 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#d96829]">
                    {t("source-grounding")}
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-800">
                    {t("osha-log-2025-q4")}
                  </p>
                </div>
                <div className="rounded-2xl bg-[#ff8b49]/10 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#d96829]">
                    {t("export-ready")}
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-800">
                    {t("output-formatted-for-word")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#ff8b49]">
              {t("how-it-works")}
            </p>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              {t("ask-questions-about-incidents")}
            </h2>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {steps.map((item) => (
              <div
                key={item.number}
                className="rounded-[28px] border border-black/8 bg-[#f7f3ed] p-7 shadow-[0_20px_60px_rgba(15,23,42,0.05)]"
              >
                <div className="text-4xl font-semibold tracking-tight text-[#ff8b49]">
                  {item.number}
                </div>
                <h3 className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
                  {item.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-slate-600">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCtaSection />
    </main>
  );
}
